/**
 * Client wrappers for the academic director's vetting API (`/api/vet`) and
 * the admin control that opens a vetted topic to students
 * (`/api/admin/chemistry-packs`). Every request and response type lives in
 * `shared/vetting/api.ts`; the paths come from its `VET_API_PATHS` and
 * `CONTENT_RELEASE_PATHS`, which are relative to `/api` like the API base.
 *
 * Each call takes the Auth0 token, as `chemistryPracticeApi` does;
 * `fetchWithConfig` attaches it and handles refresh, retries and the server's
 * `{ code, message }` errors (a 409 release refusal keeps its `waiting` list
 * on the thrown error). Writes never replay automatically: a decision, a
 * comment or a wording is recorded once.
 */
import {
  CONTENT_RELEASE_PATHS,
  type ContentReleaseListResponse,
  type ContentReleaseRefusal,
  type ContentReleaseRequest,
  type ContentReleaseResponse,
  VET_API_PATHS,
  type VetAccessCodeSummary,
  type VetCommentCreateRequest,
  type VetCommentEditRequest,
  type VetCommentListResponse,
  type VetCommentReplyRequest,
  type VetCommentResolveRequest,
  type VetCommentResponse,
  type VetDecideRequest,
  type VetDecideResponse,
  type VetFlagAnswerRequest,
  type VetFlagAnswerResponse,
  type VetFlagListResponse,
  type VetFlagPayload,
  type VetIntroSeenResponse,
  type VetItemPayload,
  type VetQueueHome,
  type VetRecentResponse,
  type VetSectionPayload,
  type VetTargetRef,
  type VetUndoRequest,
  type VetUndoResponse,
  type VetVariationsPayload,
  type VetWordingCreateRequest,
  type VetWordingCreateResponse,
  type VetWordingListResponse,
} from '@shared/vetting/api';

import {
  apiErrorCode,
  authenticatedRequestUrl,
  fetchWithConfig,
} from './apiUtils';

/** One item, notes section or set of practice variations, by its kind. */
export type VetTargetPayload =
  | VetItemPayload
  | VetSectionPayload
  | VetVariationsPayload;

const send = (method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  retry: false,
});

const withQuery = (
  path: string,
  params: Record<string, string | undefined>
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value);
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

export const vetApi = {
  /** The queue home: Back to you, the topics with their counts, Recent. */
  getQueue: (authToken: string) =>
    fetchWithConfig<VetQueueHome>(VET_API_PATHS.queue, { authToken }),

  /** The director's last twenty decisions, newest first. */
  getRecent: (authToken: string) =>
    fetchWithConfig<VetRecentResponse>(VET_API_PATHS.recent, { authToken }),

  getItem: (packId: string, itemId: string, authToken: string) =>
    fetchWithConfig<VetItemPayload>(VET_API_PATHS.item(packId, itemId), {
      authToken,
    }),

  getSection: (packId: string, sectionId: string, authToken: string) =>
    fetchWithConfig<VetSectionPayload>(
      VET_API_PATHS.section(packId, sectionId),
      { authToken }
    ),

  /** Three sample questions per page; "See three more" asks for the next page. */
  getVariations: (
    packId: string,
    familyId: string,
    page: number,
    authToken: string
  ) =>
    fetchWithConfig<VetVariationsPayload>(
      VET_API_PATHS.variations(packId, familyId, page),
      { authToken }
    ),

  /** Whichever of the three a target is. */
  getTarget: (
    target: VetTargetRef,
    authToken: string,
    page = 0
  ): Promise<VetTargetPayload> => {
    switch (target.kind) {
      case 'item':
        return vetApi.getItem(target.packId, target.id, authToken);
      case 'section':
        return vetApi.getSection(target.packId, target.id, authToken);
      case 'drill_family':
        return vetApi.getVariations(target.packId, target.id, page, authToken);
    }
  },

  decide: (body: VetDecideRequest, authToken: string) =>
    fetchWithConfig<VetDecideResponse>(VET_API_PATHS.decisions, {
      ...send('POST', body),
      authToken,
    }),

  /** Undoes the director's newest decision; it stays undoable with no timer. */
  undo: (body: VetUndoRequest, authToken: string) =>
    fetchWithConfig<VetUndoResponse>(VET_API_PATHS.undo, {
      ...send('POST', body),
      authToken,
    }),

  /** Closes the three-step card for good. */
  markIntroSeen: (authToken: string) =>
    fetchWithConfig<VetIntroSeenResponse>(VET_API_PATHS.introSeen, {
      ...send('POST'),
      authToken,
    }),

  listComments: (target: VetTargetRef, authToken: string) =>
    fetchWithConfig<VetCommentListResponse>(
      withQuery(VET_API_PATHS.comments, {
        packId: target.packId,
        kind: target.kind,
        targetId: target.id,
      }),
      { authToken }
    ),

  createComment: (body: VetCommentCreateRequest, authToken: string) =>
    fetchWithConfig<VetCommentResponse>(VET_API_PATHS.comments, {
      ...send('POST', body),
      authToken,
    }),

  editComment: (
    commentId: string,
    body: VetCommentEditRequest,
    authToken: string
  ) =>
    fetchWithConfig<VetCommentResponse>(VET_API_PATHS.comment(commentId), {
      ...send('PATCH', body),
      authToken,
    }),

  /** 204: the thread is gone. */
  deleteComment: (commentId: string, authToken: string) =>
    fetchWithConfig<null>(VET_API_PATHS.comment(commentId), {
      ...send('DELETE'),
      authToken,
    }),

  replyToComment: (
    commentId: string,
    body: VetCommentReplyRequest,
    authToken: string
  ) =>
    fetchWithConfig<VetCommentResponse>(
      VET_API_PATHS.commentReplies(commentId),
      { ...send('POST', body), authToken }
    ),

  editReply: (
    commentId: string,
    replyId: string,
    body: VetCommentEditRequest,
    authToken: string
  ) =>
    fetchWithConfig<VetCommentResponse>(
      VET_API_PATHS.commentReply(commentId, replyId),
      { ...send('PATCH', body), authToken }
    ),

  /** Answers with the thread as it stands without the reply. */
  deleteReply: (commentId: string, replyId: string, authToken: string) =>
    fetchWithConfig<VetCommentResponse>(
      VET_API_PATHS.commentReply(commentId, replyId),
      { ...send('DELETE'), authToken }
    ),

  /** `resolved: false` reopens the thread. */
  resolveComment: (
    commentId: string,
    body: VetCommentResolveRequest,
    authToken: string
  ) =>
    fetchWithConfig<VetCommentResponse>(
      VET_API_PATHS.commentResolve(commentId),
      { ...send('POST', body), authToken }
    ),

  listWordings: (
    params: { packId: string; itemId?: string },
    authToken: string
  ) =>
    fetchWithConfig<VetWordingListResponse>(
      withQuery(VET_API_PATHS.wordings, params),
      { authToken }
    ),

  /** "Add a wording that should count". */
  addWording: (body: VetWordingCreateRequest, authToken: string) =>
    fetchWithConfig<VetWordingCreateResponse>(VET_API_PATHS.wordings, {
      ...send('POST', body),
      authToken,
    }),

  listFlags: (authToken: string) =>
    fetchWithConfig<VetFlagListResponse>(VET_API_PATHS.flags, { authToken }),

  getFlag: (flagId: string, authToken: string) =>
    fetchWithConfig<VetFlagPayload>(VET_API_PATHS.flag(flagId), { authToken }),

  answerFlag: (flagId: string, body: VetFlagAnswerRequest, authToken: string) =>
    fetchWithConfig<VetFlagAnswerResponse>(VET_API_PATHS.flagAnswer(flagId), {
      ...send('POST', body),
      authToken,
    }),

  /**
   * Signed out: what a director's code grants, read out before Google
   * sign-in. 404 (`vetting_code_not_found`) for any code that is not ready.
   */
  getAccessCodeSummary: (code: string) =>
    fetchWithConfig<VetAccessCodeSummary>(
      VET_API_PATHS.accessCodeSummary(code.trim().toUpperCase()),
      { retry: false }
    ),
};

/**
 * A payload's scan link as an absolute URL: the server sends a signed path
 * under the API base, as the student pages receive it; `ChemistryFigure`
 * appends the figure's file to it.
 */
export function vetMediaBase(
  mediaBaseUrl: string | undefined
): string | undefined {
  if (!mediaBaseUrl) return undefined;
  if (/^https?:\/\//.test(mediaBaseUrl)) return mediaBaseUrl;
  return authenticatedRequestUrl(
    mediaBaseUrl.startsWith('/') ? mediaBaseUrl : `/${mediaBaseUrl}`
  );
}

export const contentReleaseApi = {
  list: (authToken: string) =>
    fetchWithConfig<ContentReleaseListResponse>(CONTENT_RELEASE_PATHS.list, {
      authToken,
    }),

  /** 409 {@link ContentReleaseRefusal} while anything in the topic is not vetted. */
  release: (packId: string, body: ContentReleaseRequest, authToken: string) =>
    fetchWithConfig<ContentReleaseResponse>(
      CONTENT_RELEASE_PATHS.release(packId),
      { ...send('POST', body), authToken }
    ),

  withdraw: (packId: string, authToken: string) =>
    fetchWithConfig<ContentReleaseResponse>(
      CONTENT_RELEASE_PATHS.withdraw(packId),
      { ...send('POST'), authToken }
    ),
};

/** The release refusal a 409 carries: the server's sentence and what still waits. */
export function releaseRefusalOf(error: unknown): ContentReleaseRefusal | null {
  if (apiErrorCode(error) !== 'not_all_vetted') return null;
  const body = error as Partial<ContentReleaseRefusal> & Error;
  return {
    message: body.message,
    code: 'not_all_vetted',
    waiting: Array.isArray(body.waiting) ? body.waiting : [],
  };
}

/** The server's plain sentence for a failed call, or the fallback. */
export function vetErrorMessage(error: unknown, fallback: string): string {
  const message =
    error instanceof Error && typeof error.message === 'string'
      ? error.message
      : '';
  // A network or unstructured failure reads "Server error: 502" or similar;
  // only the server's own plain sentences reach the director.
  if (!message || /^(Server error|API request failed)/.test(message)) {
    return fallback;
  }
  return message;
}
